import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator } from "namirasoft-core";
import { AgentInputRow, AgentRow, LLMListItemRow, LLMProviderType, } from "namirasoft-expert";
import { CacheRow, CloudRow, ContainerRow, DatabaseRow, EnvironmentRow, KubernetesRow, MessagingRow, MetricRow, ProjectRow, ServerRow, StreamingRow } from "namirasoft-infra";
import { LogGroupRow } from "namirasoft-log";
import { TopicRow } from "namirasoft-notification-sender";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxCombo, NSBoxDouble, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSLabel } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ExpertEntities } from "../ExpertEntities";
import { ExpertLayout } from '../ExpertLayout';
import { ExpertRouterProps } from "../ExpertRouterProps";

interface AgentEditPageProps extends NSARouterMakerResult, ExpertRouterProps
{
    isEdit: boolean;
}

interface AgentEditPageState
{
    selected_project_id?: string;
    selected_environment_id?: string;
    selected_llm_mini_model_name?: string;
    selected_llm_full_model_name?: string;
    llm_models: LLMListItemRow[];
}

export default class AgentEditPage extends Component<AgentEditPageProps, AgentEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEntity_ProjectID = createRef<NSBoxEntity<ProjectRow>>();
    NSBoxEntity_EnvironmentID = createRef<NSBoxEntity<EnvironmentRow>>();
    NSBoxEntity_FilterCloudIDs = createRef<NSBoxEntity<CloudRow>>();
    NSBoxEntity_FilterServerIDs = createRef<NSBoxEntity<ServerRow>>();
    NSBoxEntity_FilterKubernetesIDs = createRef<NSBoxEntity<KubernetesRow>>();
    NSBoxEntity_FilterContainerIDs = createRef<NSBoxEntity<ContainerRow>>();
    NSBoxEntity_FilterDatabaseIDs = createRef<NSBoxEntity<DatabaseRow>>();
    NSBoxEntity_FilterCacheIDs = createRef<NSBoxEntity<CacheRow>>();
    NSBoxEntity_FilterMessagingIDs = createRef<NSBoxEntity<MessagingRow>>();
    NSBoxEntity_FilterStreamingIDs = createRef<NSBoxEntity<StreamingRow>>();
    NSBoxEntity_FilterMetricIDs = createRef<NSBoxEntity<MetricRow>>();
    NSBoxEntity_TargetTopicID = createRef<NSBoxEntity<TopicRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();
    NSBoxDouble_MaximumCostPerWeek = createRef<NSBoxDouble>();
    NSBoxDouble_MaximumCostPerDay = createRef<NSBoxDouble>();
    NSBoxDouble_MaximumCostPerChat = createRef<NSBoxDouble>();
    NSBoxDouble_MaximumCostPerRun = createRef<NSBoxDouble>();
    NSBoxEnum_LLMMiniProvider = createRef<NSBoxEnum<LLMProviderType>>();
    NSBoxCombo_LLMMiniModel = createRef<NSBoxCombo>();
    NSBoxEnum_LLMFullProvider = createRef<NSBoxEnum<LLMProviderType>>();
    NSBoxCombo_LLMFullModel = createRef<NSBoxCombo>();

    constructor(props: AgentEditPageProps)
    {
        super(props);
        this.state = { llm_models: [] };
    }

    override async componentDidMount()
    {
        try
        {
            const res = await this.props.servers.expert.llm.List();
            this.setState({ llm_models: res.rows });
        }
        catch (err)
        {
            console.error(err);
        }
    }

    private getModelsByProvider(provider: LLMProviderType | undefined)
    {
        if (!provider)
            return [];
        return this.state.llm_models.filter(m => m.provider_name === provider);
    }

    private renderModelPricing(modelName: string | undefined)
    {
        if (!modelName)
            return null;
        const model = this.state.llm_models.find(m => m.model_name === modelName);
        if (!model)
            return null;
        return (
            <>
                <NSLabel title="Input Price (per 1M tokens)" description={`$${model.price_input}`} />
                <NSLabel title="Output Price (per 1M tokens)" description={`$${model.price_output}`} />
            </>
        );
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <ExpertLayout<AgentRow, AgentInputRow>
                {...this.props}
                pages={{
                    entity: ExpertEntities.agent(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "LLM",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<LLMProviderType>
                                                        ref={this.NSBoxEnum_LLMMiniProvider}
                                                        title="LLM Mini Provider"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => LLMProviderType}
                                                        onChanged={() =>
                                                        {
                                                            this.NSBoxCombo_LLMMiniModel.current?.setValue(null);
                                                            this.NSBoxCombo_LLMMiniModel.current?.reload();
                                                            this.setState({ selected_llm_mini_model_name: undefined });
                                                        }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxCombo
                                                        ref={this.NSBoxCombo_LLMMiniModel}
                                                        title="LLM Mini Model"
                                                        required
                                                        multiple={false}
                                                        getOptions={() =>
                                                        {
                                                            let llm_mini_provider_name = this.NSBoxEnum_LLMMiniProvider.current?.getValueEnumOne(LLMProviderType, LLMProviderType.Anthropic, false) ?? LLMProviderType.Anthropic;
                                                            return this.getModelsByProvider(llm_mini_provider_name).map(m => ({ value: m.model_name, title: m.model_name }));
                                                        }}
                                                        onChanged={(box) =>
                                                        {
                                                            this.setState({ selected_llm_mini_model_name: box.getValueOne(false) ?? undefined });
                                                        }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {this.renderModelPricing(this.state.selected_llm_mini_model_name)}
                                                    <NSBoxEnum<LLMProviderType>
                                                        ref={this.NSBoxEnum_LLMFullProvider}
                                                        title="LLM Full Provider"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => LLMProviderType}
                                                        onChanged={() =>
                                                        {
                                                            this.NSBoxCombo_LLMFullModel.current?.setValue(null);
                                                            this.NSBoxCombo_LLMFullModel.current?.reload();
                                                            this.setState({ selected_llm_full_model_name: undefined });
                                                        }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxCombo
                                                        ref={this.NSBoxCombo_LLMFullModel}
                                                        title="LLM Full Model"
                                                        required
                                                        multiple={false}
                                                        getOptions={() =>
                                                        {
                                                            let llm_full_provider_name = this.NSBoxEnum_LLMFullProvider.current?.getValueEnumOne(LLMProviderType, LLMProviderType.Anthropic, false) ?? LLMProviderType.Anthropic;
                                                            return this.getModelsByProvider(llm_full_provider_name).map(m => ({ value: m.model_name, title: m.model_name }));
                                                        }}
                                                        onChanged={(box) =>
                                                        {
                                                            this.setState({ selected_llm_full_model_name: box.getValueOne(false) ?? undefined });
                                                        }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {this.renderModelPricing(this.state.selected_llm_full_model_name)}
                                                </NSColumn>
                                            ),
                                        },
                                        {
                                            title: "Filter",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    {
                                                        (!this.state.selected_project_id || !this.state.selected_environment_id) &&
                                                        <NSLabel
                                                            title="Note"
                                                            description="Please select a Project and an Environment in the General tab first to be able to fill this section."
                                                            style={{ width: NSBox.width.double, padding: 0 }}
                                                        />
                                                    }
                                                    <NSBoxEntity<CloudRow>
                                                        ref={this.NSBoxEntity_FilterCloudIDs}
                                                        title="Cloud"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="cloud"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.cloud,
                                                                    namirasoft.infra.tables.cloud.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.cloud.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxEntity<ServerRow>
                                                        ref={this.NSBoxEntity_FilterServerIDs}
                                                        title="Server"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="server"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.server,
                                                                    namirasoft.infra.tables.server.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.server.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxEntity<KubernetesRow>
                                                        ref={this.NSBoxEntity_FilterKubernetesIDs}
                                                        title="Kubernetes"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="kubernetes"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.kubernetes,
                                                                    namirasoft.infra.tables.kubernetes.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.kubernetes.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxEntity<ContainerRow>
                                                        ref={this.NSBoxEntity_FilterContainerIDs}
                                                        title="Container"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="container"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.container,
                                                                    namirasoft.infra.tables.container.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.container.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxEntity<DatabaseRow>
                                                        ref={this.NSBoxEntity_FilterDatabaseIDs}
                                                        title="Database"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="database"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.database,
                                                                    namirasoft.infra.tables.database.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.database.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxEntity<CacheRow>
                                                        ref={this.NSBoxEntity_FilterCacheIDs}
                                                        title="Cache"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="cache"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.cache,
                                                                    namirasoft.infra.tables.cache.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.cache.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxEntity<MessagingRow>
                                                        ref={this.NSBoxEntity_FilterMessagingIDs}
                                                        title="Messaging"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="messaging"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.messaging,
                                                                    namirasoft.infra.tables.messaging.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.messaging.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxEntity<StreamingRow>
                                                        ref={this.NSBoxEntity_FilterStreamingIDs}
                                                        title="Streaming"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="streaming"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.streaming,
                                                                    namirasoft.infra.tables.streaming.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.streaming.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxEntity<MetricRow>
                                                        ref={this.NSBoxEntity_FilterMetricIDs}
                                                        title="Metric"
                                                        required={false}
                                                        multiple={true}
                                                        disabled={!this.state.selected_project_id || !this.state.selected_environment_id}
                                                        table_name="metric"
                                                        list={async (filters, page, size, sorts) =>
                                                        {
                                                            if (!this.state.selected_project_id || !this.state.selected_environment_id)
                                                                return { rows: [], count: 0 };

                                                            if (!filters)
                                                                filters = [];

                                                            if (this.state.selected_environment_id)
                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.metric,
                                                                    namirasoft.infra.tables.metric.columns.environment_id,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    this.state.selected_environment_id,
                                                                ));

                                                            return await this.props.servers.infra.metric.List(filters, page, size, sorts, null);
                                                        }}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                </NSColumn>
                                            ),
                                        },
                                        {
                                            title: "Maximum Cost",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxDouble
                                                        ref={this.NSBoxDouble_MaximumCostPerWeek}
                                                        title="Maximum Cost Per Week"
                                                        required={false}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxDouble
                                                        ref={this.NSBoxDouble_MaximumCostPerDay}
                                                        title="Maximum Cost Per Day"
                                                        required={false}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxDouble
                                                        ref={this.NSBoxDouble_MaximumCostPerChat}
                                                        title="Maximum Cost Per Chat"
                                                        required={false}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxDouble
                                                        ref={this.NSBoxDouble_MaximumCostPerRun}
                                                        title="Maximum Cost Per Run"
                                                        required={false}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                </NSColumn>
                                            ),
                                        },
                                    ],
                                },
                                editor: {
                                    getInputRow: (): AgentInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let project_id = this.NSBoxEntity_ProjectID.current?.getValueOne() ?? "";
                                        let environment_id = this.NSBoxEntity_EnvironmentID.current?.getValueOne() ?? "";
                                        let filter_cloud_ids = this.NSBoxEntity_FilterCloudIDs.current?.getValueAll() ?? "";
                                        let filter_server_ids = this.NSBoxEntity_FilterServerIDs.current?.getValueAll() ?? "";
                                        let filter_kubernetes_ids = this.NSBoxEntity_FilterKubernetesIDs.current?.getValueAll() ?? "";
                                        let filter_container_ids = this.NSBoxEntity_FilterContainerIDs.current?.getValueAll() ?? "";
                                        let filter_database_ids = this.NSBoxEntity_FilterDatabaseIDs.current?.getValueAll() ?? "";
                                        let filter_cache_ids = this.NSBoxEntity_FilterCacheIDs.current?.getValueAll() ?? "";
                                        let filter_messaging_ids = this.NSBoxEntity_FilterMessagingIDs.current?.getValueAll() ?? "";
                                        let filter_streaming_ids = this.NSBoxEntity_FilterStreamingIDs.current?.getValueAll() ?? "";
                                        let filter_metric_ids = this.NSBoxEntity_FilterMetricIDs.current?.getValueAll() ?? "";
                                        let target_topic_id = this.NSBoxEntity_TargetTopicID.current?.getValueOne() ?? null;
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";
                                        let maximum_cost_per_week = this.NSBoxDouble_MaximumCostPerWeek.current?.getValue() ?? null;
                                        let maximum_cost_per_day = this.NSBoxDouble_MaximumCostPerDay.current?.getValue() ?? null;
                                        let maximum_cost_per_chat = this.NSBoxDouble_MaximumCostPerChat.current?.getValue() ?? null;
                                        let maximum_cost_per_run = this.NSBoxDouble_MaximumCostPerRun.current?.getValue() ?? null;
                                        let llm_mini_provider_name = this.NSBoxEnum_LLMMiniProvider.current?.getValueEnumOne(LLMProviderType, LLMProviderType.Anthropic) ?? LLMProviderType.Anthropic;
                                        let llm_mini_model_name = this.NSBoxCombo_LLMMiniModel.current?.getValueOne() ?? "";
                                        let llm_full_provider_name = this.NSBoxEnum_LLMFullProvider.current?.getValueEnumOne(LLMProviderType, LLMProviderType.Anthropic) ?? LLMProviderType.Anthropic;
                                        let llm_full_model_name = this.NSBoxCombo_LLMFullModel.current?.getValueOne() ?? "";

                                        return {
                                            workspace_id: "",
                                            name,
                                            project_id,
                                            environment_id,
                                            llm_mini_provider_name,
                                            llm_mini_model_name,
                                            llm_full_provider_name,
                                            llm_full_model_name,
                                            filter_cloud_ids,
                                            filter_server_ids,
                                            filter_kubernetes_ids,
                                            filter_container_ids,
                                            filter_database_ids,
                                            filter_cache_ids,
                                            filter_messaging_ids,
                                            filter_streaming_ids,
                                            filter_metric_ids,
                                            maximum_cost_per_week,
                                            maximum_cost_per_day,
                                            maximum_cost_per_chat,
                                            maximum_cost_per_run,
                                            target_topic_id,
                                            log_group_id,
                                            description,
                                            agent_category: [],
                                            agent_field: [],
                                            agent_tag: []
                                        } as any as AgentInputRow;
                                    },
                                    setRow: (row: AgentRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEntity_ProjectID.current?.setValue(row.project_id);
                                        this.NSBoxEntity_EnvironmentID.current?.setValue(row.environment_id);
                                        this.NSBoxEntity_FilterCloudIDs.current?.setValue(row.filter_cloud_ids);
                                        this.NSBoxEntity_FilterServerIDs.current?.setValue(row.filter_server_ids);
                                        this.NSBoxEntity_FilterKubernetesIDs.current?.setValue(row.filter_kubernetes_ids);
                                        this.NSBoxEntity_FilterContainerIDs.current?.setValue(row.filter_container_ids);
                                        this.NSBoxEntity_FilterDatabaseIDs.current?.setValue(row.filter_database_ids);
                                        this.NSBoxEntity_FilterCacheIDs.current?.setValue(row.filter_cache_ids);
                                        this.NSBoxEntity_FilterMessagingIDs.current?.setValue(row.filter_messaging_ids);
                                        this.NSBoxEntity_FilterStreamingIDs.current?.setValue(row.filter_streaming_ids);
                                        this.NSBoxEntity_FilterMetricIDs.current?.setValue(row.filter_metric_ids);
                                        this.NSBoxEntity_TargetTopicID.current?.setValue(row.target_topic_id);
                                        this.NSBoxEntity_LogGroupID.current?.setValue((row as any).log_group_id);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                        this.NSBoxDouble_MaximumCostPerWeek.current?.setValue(row.maximum_cost_per_week);
                                        this.NSBoxDouble_MaximumCostPerDay.current?.setValue(row.maximum_cost_per_day);
                                        this.NSBoxDouble_MaximumCostPerChat.current?.setValue(row.maximum_cost_per_chat);
                                        this.NSBoxDouble_MaximumCostPerRun.current?.setValue(row.maximum_cost_per_run);
                                        this.NSBoxEnum_LLMMiniProvider.current?.setValue(row.llm_mini_provider_name, () =>
                                        {
                                            this.NSBoxCombo_LLMMiniModel.current?.setValue(row.llm_mini_model_name);
                                            this.setState({ selected_llm_mini_model_name: row.llm_mini_model_name });
                                        });
                                        this.NSBoxEnum_LLMFullProvider.current?.setValue(row.llm_full_provider_name, () =>
                                        {
                                            this.NSBoxCombo_LLMFullModel.current?.setValue(row.llm_full_model_name);
                                            this.setState({ selected_llm_full_model_name: row.llm_full_model_name });
                                        });
                                    }
                                },
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ProjectRow>
                        ref={this.NSBoxEntity_ProjectID}
                        title="Project"
                        required
                        multiple={false}
                        table_name="project"
                        table={namirasoft.infra.tables.project}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_project_id: box.getValueOne() ?? undefined }),
                                () =>
                                {
                                    this.NSBoxEntity_EnvironmentID.current?.setValue(null);
                                    this.NSBoxEntity_EnvironmentID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<EnvironmentRow>
                        ref={this.NSBoxEntity_EnvironmentID}
                        title="Environment"
                        required
                        multiple={false}
                        table_name="environment"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_project_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.environment,
                                    namirasoft.infra.tables.environment.columns.project_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_project_id,
                                ));

                            return await this.props.servers.infra.environment.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_environment_id: box.getValueOne(false) ?? "" }),
                                () =>
                                {
                                    this.NSBoxEntity_FilterCloudIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterServerIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterKubernetesIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterContainerIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterDatabaseIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterCacheIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterMessagingIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterStreamingIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterMetricIDs.current?.setValue(null);
                                    this.NSBoxEntity_FilterCloudIDs.current?.reload();
                                    this.NSBoxEntity_FilterServerIDs.current?.reload();
                                    this.NSBoxEntity_FilterKubernetesIDs.current?.reload();
                                    this.NSBoxEntity_FilterContainerIDs.current?.reload();
                                    this.NSBoxEntity_FilterDatabaseIDs.current?.reload();
                                    this.NSBoxEntity_FilterCacheIDs.current?.reload();
                                    this.NSBoxEntity_FilterMessagingIDs.current?.reload();
                                    this.NSBoxEntity_FilterStreamingIDs.current?.reload();
                                    this.NSBoxEntity_FilterMetricIDs.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<TopicRow>
                        ref={this.NSBoxEntity_TargetTopicID}
                        title="Target Topic"
                        required={false}
                        multiple={false}
                        table_name="topic"
                        table={namirasoft.notification_sender.tables.topic}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        table_name="log_group"
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </ExpertLayout>
        );
    }
}